/**
 * HomeHiringStrip — "AI employees recently hired on CellCog" (Aug 25 2026).
 *
 * Social proof band under the fold: REAL AI-employee role titles recently hired
 * on the platform, drifting logo-wall style in two counter-moving rows.
 * Decision record: Maeve, work/hiring_strip/PRD.md (ruled live with Nitish).
 *
 * Data: recentHiresSnapshot.json — baked, regenerated by the daily hirer pull
 * (roles + FIRST INITIAL + date only; never customer identity, never counts).
 * At runtime we try the prod daily JSON (org-stats pattern) and silently fall
 * back to the snapshot. Fewer than 6 titles after filtering -> render nothing
 * (an empty proof strip is worse than none).
 *
 * The chips are STATIC TAGS — not links, no hover, no pointer (pill-grammar
 * law). Colours are the demo frame's CAST palette so the strip reads as the
 * same system as the product above it.
 */
import React, { useEffect, useState } from 'react';
import { HIRING_STRIP } from './homeContent';
import snapshot from './recentHiresSnapshot.json';
import './HomeHiringStrip.css';

/* The demo frame's CAST colours, verbatim (HomeDemoFrame.js). Ink is set in CSS. */
const CAST_COLORS = ['#7dc3c3', '#e8b45a', '#a48ce0', '#6db1f0', '#7fd6a4', '#f096b8'];

const LIVE_URL = '/assets/org-stats/recent_hires.json';
const MIN_ROLES = 6;
/* NO curation cap (Nitish, Aug 25): the generator's rolling 30-day window is the only
   regulator. 60 is a pure safety ceiling against a malformed feed, never a design cap. */
const CAP = 60;

/* Stable colour per chip: same input, same colour, across refreshes. */
const colorFor = (seed) => {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return CAST_COLORS[h % CAST_COLORS.length];
};

/* English-readable titles only: every letter Latin script (PRD §2.9). */
const latinOnly = (s) =>
  [...s].every((c) => !/\p{L}/u.test(c) || (c.codePointAt(0) || 0) < 0x250);

/* Defence in depth — the generator already enforces all of this, but the
   component must stay safe against any future data source. */
const clean = (roles) => {
  const seen = new Set();
  const out = [];
  (roles || []).forEach((r) => {
    const title = (r && typeof r.title === 'string' ? r.title : '').trim();
    const initial = (r && typeof r.initial === 'string' ? r.initial : '').trim();
    if (!title || !initial) return;
    if (!latinOnly(title)) return;
    const key = title.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ title, initial: initial[0].toUpperCase() });
  });
  return out.slice(0, CAP);
};

export default function HomeHiringStrip() {
  const [roles, setRoles] = useState(() => clean(snapshot.roles));

  /* v2 plumbing: prod emits a fresh JSON daily (org-stats pattern). Fetch
     failure of any kind = keep the baked snapshot, say nothing. */
  useEffect(() => {
    let dead = false;
    fetch(LIVE_URL, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (dead || !data || !Array.isArray(data.roles)) return;
        const fresh = clean(data.roles);
        if (fresh.length >= MIN_ROLES) setRoles(fresh);
      })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  if (roles.length < MIN_ROLES) return null;

  /* Alternate titles across the two rows so both carry fresh hires. */
  const rows = [roles.filter((_, i) => i % 2 === 0), roles.filter((_, i) => i % 2 === 1)];

  const renderSet = (row, copy) => (
    <div className="home-hs__set" aria-hidden={copy === 1 ? 'true' : undefined}>
      {row.map((r) => (
        <span className="home-hs__item" key={`${r.title}-${copy}`}>
          <span
            className="home-hs__av"
            style={{ background: colorFor(r.initial + r.title) }}
          >
            {r.initial}
          </span>
          {r.title}
        </span>
      ))}
    </div>
  );

  return (
    <section className="home-hs" aria-label={HIRING_STRIP.header}>
      <div className="home-hs__head">
        <span className="home-hs__dot" aria-hidden="true" />
        <span className="home-hs__lab">{HIRING_STRIP.header}</span>
      </div>
      <div className="home-hs__marquee">
        <div className="home-hs__track">
          {renderSet(rows[0], 0)}
          {renderSet(rows[0], 1)}
        </div>
      </div>
      <div className="home-hs__marquee home-hs__marquee--rev">
        <div className="home-hs__track home-hs__track--rev">
          {renderSet(rows[1], 0)}
          {renderSet(rows[1], 1)}
        </div>
      </div>
    </section>
  );
}
